import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FormDepartureProvider, useFormDepartureContext } from '../components/FormDepartureContext';
import FormDepartureWizard from '../components/FormDepartureWizard';
import LoadingTransition from '../components/LoadingTransition';
import { getBrandOrDefault } from '../data/map';
import { useUser } from '../utils/UserContext';
import {
  PersonalInfoSection,
  JobInfoSection,
  DatesSection,
  PhotoSection,
  ReviewSubmitSection,
} from './ticketform';

/* ============================================================================
 * DepartureTicketForm — ticket wizard page for every client.
 *
 * Personal Information comes straight from the session (no API fetch):
 *   Company      = user.userJob   (for a client, userJob IS their business name)
 *   Request Name = user.userName
 *   Email        = user.email
 * location.state can still override any of those (e.g. a pre-filled link).
 *
 * Brand (logo + accent): session brand → BRANDS lookup by group/userJob →
 * client headshot (their logo on file) → SNL default. See map.js.
 * ==========================================================================*/

// Renders whichever section matches currentStep, inside the wizard shell
const FormStepRenderer = () => {
  const { currentStep } = useFormDepartureContext();
  const steps = [PersonalInfoSection, JobInfoSection, DatesSection, PhotoSection, ReviewSubmitSection];
  const Step = steps[currentStep] || PersonalInfoSection;
  return (
    <FormDepartureWizard>
      <Step />
    </FormDepartureWizard>
  );
};

const DepartureTicketForm = () => {
  const location = useLocation();
  const overrides = location.state || {};
  const { user } = useUser();

  const [showLoadingTransition, setShowLoadingTransition] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShowLoadingTransition(false), 1200);
    return () => clearTimeout(t);
  }, []);

  const brand = getBrandOrDefault(user);

  // Session → form fields. Company is the client's business (userJob).
  const initialFormData = {
    company:     user?.userJob  || '',
    requestName: user?.userName || '',
    email:       user?.email    || '',
    ...overrides,

    jobTitle: '',
    jobType: '',
    jobDescription: '',
    completedDate: '',
    completedTime: '',
    photos: [],

    startdate: '',
    returnDate: '',
    clientApproval: 'Pending Approval',
    activeTicket: 'Active Ticket',
    status: 'Pending',
  };

  if (showLoadingTransition || !user) {
    return (
      <LoadingTransition
        companylogo={brand.logoDark || brand.logo}
        message={!user ? 'Loading your data, please wait…' : `Preparing your ${brand.label} ticket form…`}
      />
    );
  }

  return (
    <FormDepartureProvider userData={initialFormData} brand={brand}>
      <FormStepRenderer />
    </FormDepartureProvider>
  );
};

export default DepartureTicketForm;